// Cíl odkazů „Přihlásit se“ na landing page. Produkce na trenigo.cz nic nenastavuje a odkazy vedou
// na https://app.trenigo.cz/login (FR01 20261001_LandingPage/002_Marketing, FR-08). Testovací web
// (např. tranigo.cz) nastaví build-time proměnnou VITE_APP_LOGIN_URL v env svého Netlify site, aby
// nepřesměroval do produkce - rozhodnutí FR01 20261003_ZmenaDomeny OR-02 A (apps/landing přebírá
// konfigurovatelnou URL přihlášení z apps/web).
//
// Pravidla jsou shodná s apps/web/src/features/marketing/marketing-config.ts (parseAppLoginUrl).
// Landing nesmí záviset na apps/web, proto vlastní kopie; kopie v apps/web zanikne s odstraněním
// landing page z aplikace (002_Marketing/FR03). Modul načítá i vite.config.ts v Node, proto se
// `import.meta.env` čte jen uvnitř readLoginUrl().

export const DEFAULT_APP_LOGIN_URL = 'https://app.trenigo.cz/login';
export const APP_LOGIN_URL_ENV = 'VITE_APP_LOGIN_URL';

export type LoginUrlParseResult = { ok: true; value: string } | { ok: false; error: string };

export interface LoginUrlParseOptions {
  /** `http:` je povolené jen mimo produkční build (vývojový server). */
  allowHttp: boolean;
}

export function parseAppLoginUrl(raw: string | undefined, options: LoginUrlParseOptions): LoginUrlParseResult {
  const value = raw?.trim() ?? '';
  if (value === '') {
    return { ok: true, value: DEFAULT_APP_LOGIN_URL };
  }
  const fail = (reason: string): LoginUrlParseResult => ({
    ok: false,
    error: `${APP_LOGIN_URL_ENV}: hodnota "${value}" ${reason}.`,
  });
  if (/\s/.test(value)) {
    return fail('obsahuje mezeru');
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return fail('není absolutní URL (očekává se např. "https://app.tranigo.cz/login")');
  }
  if (url.protocol === 'http:' && !options.allowHttp) {
    return fail('používá schéma "http:", které je povolené jen mimo produkční build; použijte "https:"');
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return fail(`používá nepovolené schéma "${url.protocol}"; povolené je jen "https:"`);
  }
  // "https:host/x" bez lomítek URL parser přijme, ale v href by ji prohlížeč vyhodnotil relativně.
  if (!/^https?:\/\//i.test(value) || url.hostname === '') {
    return fail('není absolutní URL (očekává se např. "https://app.tranigo.cz/login")');
  }
  return { ok: true, value };
}

/**
 * Runtime hodnota pro komponenty. Produkční build s neplatnou hodnotou neprojde (vite.config.ts),
 * ve vývoji se po varování použije výchozí URL.
 */
export function readLoginUrl(): string {
  const result = parseAppLoginUrl(import.meta.env.VITE_APP_LOGIN_URL, { allowHttp: !import.meta.env.PROD });
  return result.ok ? result.value : DEFAULT_APP_LOGIN_URL;
}
